/**
 * Single entry-point for all affiliate CTA clicks. Fires the canonical
 * `affiliate_click` PostHog event (ADR-0063 + ADR-0064), then hands off
 * to `Linking.openURL` to leave the app. Surfaces never call `Linking`
 * directly — they call this helper so attribution measurement and
 * navigation stay in lockstep.
 */

import { Linking } from 'react-native';
import {
  type BuildAffiliateEventInput,
  buildAffiliateClickEvent,
  buildAffiliateImpressionEvent,
} from '@my-fullstack-app/affiliates';
import { track } from '../analytics';

export interface TrackAndOpenInput extends BuildAffiliateEventInput {
  url: string;
}

/** Fire a single `affiliate_impression` event. Callers typically invoke
 *  this once from a `useEffect` on mount of a CTA surface — the event
 *  provides the denominator for CTR against `affiliate_click`. */
export function trackAffiliateImpression(input: BuildAffiliateEventInput): void {
  const event = buildAffiliateImpressionEvent(input);
  track(event.name, event.properties);
}

/**
 * Track the affiliate click and open the URL externally. Returns a promise
 * that resolves when `Linking.openURL` completes; callers can `void` it.
 *
 * Callers MUST pass a non-empty `url` — the helper does not re-validate.
 * Every UI surface gates on `provider.id !== 'noop'` and on builder return
 * values (e.g. `buildFlightSearchUrl` returns `null` when IATA codes are
 * missing) before reaching this function.
 */
export async function trackAffiliateClickAndOpen(input: TrackAndOpenInput): Promise<void> {
  const { url, ...eventInput } = input;
  const event = buildAffiliateClickEvent(eventInput);
  track(event.name, event.properties);
  await Linking.openURL(url);
}
